/**
 * Vera Ideas — BFF API client (`/api/ideas/*`).
 *
 * All state-changing calls go through `bffFetch` (CSRF header + cookies).
 * Field names mirror the BFF row shapes (snake_case, like the RFP client).
 */

import { bffFetch } from '@shared/api/bffFetch';

export const IDEA_STATUSES = [
  'new',
  'under_review',
  'planned',
  'in_progress',
  'shipped',
  'not_planned',
] as const;
export type IdeaStatus = (typeof IDEA_STATUSES)[number];
export type IdeaSort = 'trending' | 'top' | 'new';

export interface IdeaTag {
  id: number;
  name: string;
  emoji: string | null;
}

export interface IdeaListItem {
  id: string;
  title: string;
  description: string | null;
  submitter_id: string;
  submitter_name: string;
  status: IdeaStatus;
  planned_date: string | null;
  created_at: string;
  shipped_at: string | null;
  vote_count: number;
  comment_count: number;
  viewer_has_voted: boolean;
  tags: IdeaTag[];
}

export interface IdeaComment {
  id: string;
  author_id: string;
  author_name: string;
  body: string;
  created_at: string;
}

export interface IdeaStatusEvent {
  from_status: IdeaStatus | null;
  to_status: IdeaStatus;
  planned_date: string | null;
  changed_by_name: string;
  note: string | null;
  created_at: string;
}

export interface IdeaDetail extends IdeaListItem {
  comments: IdeaComment[];
  history: IdeaStatusEvent[];
}

export interface SimilarIdea {
  id: string;
  title: string;
  status: IdeaStatus;
  vote_count: number;
  viewer_has_voted: boolean;
  similarity: number;
}

export interface IdeasMe {
  oid: string;
  name: string;
  isAdmin: boolean;
  convertToTicketEnabled: boolean;
}

export interface IdeaNotification {
  id: string;
  idea_id: string;
  kind: 'status_change' | 'new_comment' | 'idea_shipped' | 'idea_merged';
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface TileStats {
  new_this_month: number;
  shipped_this_quarter: number;
  open_ideas: number;
  top_idea: { id: number; title: string; votes: number } | null;
}

export interface IdeasPage {
  ideas: IdeaListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListIdeasParams {
  sort: IdeaSort;
  statuses?: IdeaStatus[];
  tagId?: number;
  q?: string;
  page?: number;
  pageSize?: number;
}

async function readError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(body.error || `${fallback} (${res.status})`);
}

export async function fetchIdeas(params: ListIdeasParams): Promise<IdeasPage> {
  const search = new URLSearchParams({ sort: params.sort });
  if (params.statuses?.length) search.set('status', params.statuses.join(','));
  if (params.tagId !== undefined) search.set('tag', String(params.tagId));
  if (params.q) search.set('q', params.q);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  const res = await bffFetch(`/api/ideas?${search.toString()}`);
  if (!res.ok) await readError(res, 'Failed to load ideas');
  return (await res.json()) as IdeasPage;
}

export type IdeaDetailResult = { idea: IdeaDetail } | { redirectTo: string };

export async function fetchIdea(id: string): Promise<IdeaDetailResult> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}`);
  if (!res.ok) await readError(res, 'Failed to load idea');
  return (await res.json()) as IdeaDetailResult;
}

export interface NewIdea {
  title: string;
  description?: string;
  tagIds?: number[];
}

export async function createIdea(input: NewIdea): Promise<{ id: string }> {
  const res = await bffFetch('/api/ideas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) await readError(res, 'Failed to submit idea');
  return ((await res.json()) as { idea: { id: string } }).idea;
}

export interface IdeaPatch {
  title?: string;
  description?: string;
  tagIds?: number[];
}

export async function updateIdea(id: string, patch: IdeaPatch): Promise<void> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) await readError(res, 'Failed to update idea');
}

export async function deleteIdea(id: string): Promise<void> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete idea');
}

export interface VoteResult {
  voteCount: number;
  viewerHasVoted: boolean;
}

export async function addVote(id: string): Promise<VoteResult> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}/vote`, { method: 'PUT' });
  if (!res.ok) await readError(res, 'Failed to vote');
  return (await res.json()) as VoteResult;
}

export async function removeVote(id: string): Promise<VoteResult> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}/vote`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to remove vote');
  return (await res.json()) as VoteResult;
}

export async function addComment(ideaId: string, body: string): Promise<IdeaComment> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(ideaId)}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) await readError(res, 'Failed to post comment');
  return ((await res.json()) as { comment: IdeaComment }).comment;
}

export async function deleteComment(commentId: string): Promise<void> {
  const res = await bffFetch(`/api/ideas/comments/${encodeURIComponent(commentId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) await readError(res, 'Failed to delete comment');
}

export async function fetchSimilar(title: string): Promise<SimilarIdea[]> {
  const res = await bffFetch(`/api/ideas/similar?title=${encodeURIComponent(title)}`);
  if (!res.ok) await readError(res, 'Failed to check for similar ideas');
  return ((await res.json()) as { ideas: SimilarIdea[] }).ideas;
}

export async function fetchTags(): Promise<IdeaTag[]> {
  const res = await bffFetch('/api/ideas/tags');
  if (!res.ok) await readError(res, 'Failed to load tags');
  return ((await res.json()) as { tags: IdeaTag[] }).tags;
}

export async function fetchMe(): Promise<IdeasMe> {
  const res = await bffFetch('/api/ideas/me');
  if (!res.ok) await readError(res, 'Failed to load viewer info');
  return (await res.json()) as IdeasMe;
}

export async function fetchNotifications(): Promise<{
  notifications: IdeaNotification[];
  unreadCount: number;
}> {
  const res = await bffFetch('/api/ideas/notifications');
  if (!res.ok) await readError(res, 'Failed to load notifications');
  return (await res.json()) as { notifications: IdeaNotification[]; unreadCount: number };
}

export async function markNotificationsRead(): Promise<void> {
  const res = await bffFetch('/api/ideas/notifications/read', { method: 'POST' });
  if (!res.ok) await readError(res, 'Failed to mark notifications read');
}

export async function fetchTileStats(): Promise<TileStats> {
  const res = await bffFetch('/api/ideas/tile-stats');
  if (!res.ok) await readError(res, 'Failed to load idea stats');
  return ((await res.json()) as { stats: TileStats }).stats;
}

export interface StatusChange {
  status: IdeaStatus;
  plannedDate?: string | null;
  note?: string;
}

export async function setIdeaStatus(id: string, change: StatusChange): Promise<void> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(change),
  });
  if (!res.ok) await readError(res, 'Failed to update status');
}

export async function mergeIdea(id: string, intoIdeaId: string): Promise<{ intoIdeaId: string }> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ intoIdeaId }),
  });
  if (!res.ok) await readError(res, 'Failed to merge idea');
  return (await res.json()) as { intoIdeaId: string };
}

export async function convertToTicket(id: string): Promise<void> {
  const res = await bffFetch(`/api/ideas/${encodeURIComponent(id)}/convert-to-ticket`, {
    method: 'POST',
  });
  if (!res.ok) await readError(res, 'Convert to ticket is not available yet');
}

// ---------------------------------------------------------------------------
// Rotating prompts (board header copy; admin-managed)
// ---------------------------------------------------------------------------

export interface IdeaPrompt {
  id: number;
  text: string;
  active: boolean;
  display_order: number;
}

export async function fetchPrompts(): Promise<Array<Pick<IdeaPrompt, 'id' | 'text'>>> {
  const res = await bffFetch('/api/ideas/prompts');
  if (!res.ok) await readError(res, 'Failed to load prompts');
  return ((await res.json()) as { prompts: Array<Pick<IdeaPrompt, 'id' | 'text'>> }).prompts;
}

export async function fetchAdminPrompts(): Promise<IdeaPrompt[]> {
  const res = await bffFetch('/api/ideas/admin/prompts');
  if (!res.ok) await readError(res, 'Failed to load prompts');
  return ((await res.json()) as { prompts: IdeaPrompt[] }).prompts;
}

export async function createPrompt(text: string): Promise<IdeaPrompt> {
  const res = await bffFetch('/api/ideas/admin/prompts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) await readError(res, 'Failed to add prompt');
  return ((await res.json()) as { prompt: IdeaPrompt }).prompt;
}

export interface PromptPatch {
  text?: string;
  active?: boolean;
  displayOrder?: number;
}

export async function updatePrompt(id: number, patch: PromptPatch): Promise<void> {
  const res = await bffFetch(`/api/ideas/admin/prompts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) await readError(res, 'Failed to update prompt');
}

export async function deletePrompt(id: number): Promise<void> {
  const res = await bffFetch(`/api/ideas/admin/prompts/${id}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete prompt');
}

// ---------------------------------------------------------------------------
// Admin summary
// ---------------------------------------------------------------------------

export interface AdminSummary {
  countsByStatus: Record<string, number>;
  oldestUntriaged: { id: string; title: string; created_at: string } | null;
  medianDaysInNew: number | null;
}

export async function fetchAdminSummary(): Promise<AdminSummary> {
  const res = await bffFetch('/api/ideas/admin/summary');
  if (!res.ok) await readError(res, 'Failed to load admin summary');
  return (await res.json()) as AdminSummary;
}
